<template>
  <PageContainer maxWidth="1300px">
    <!-- Summary -->
    <Card>
      <template #content>
        <VeeForm
          v-slot="{ handleSubmit }"
          :validation-schema="schema"
          as="div"
          @invalidSubmit="onInvalidSubmit"
        >
          <form
            @submit.prevent="handleSubmit($event, onSubmitOrganizationInfo)"
          >
            <h2 class="mt-0 text-xl">
              Organization
            </h2>
            <div class="grid">
              <div class="col-12">
                <BaseInput
                  v-model="organizationForm.name"
                  fieldId="name"
                  fieldName="name"
                  fieldLabel="Name"
                />
              </div>
              <div class="col-12">
                <Divider />

                <CategoryInput
                  fieldLabel="IAB Categories"
                  fieldName="iabCodes"
                  :categoryOptions="IAB_CATEGORY_OPTIONS"
                  v-model="organizationForm.iabCodes"
                  helperText="Please identify which categories apply to your brand
                    so we do not place your advertisements next to a competitive brand."
                  :disableSelectAll="true"
                />
              </div>
            </div>
            <div class="flex justify-content-end">
              <Button
                type="submit"
                :loading="organizationFormIsSubmitting"
                label="Save"
                :disabled="isSubmitOrganizationDisabled"
              />
            </div>
          </form>
        </VeeForm>
      </template>
    </Card>

    <Card
      v-if="settingsStore.paymentSystemEnabled"
      class="mt-4"
    >
      <template #content>
        <h2 class="mt-0 text-xl">
          Payment
        </h2>
        <AvailablePaymentMessage
          v-if="showAvailablePaymentMessage"
        />
        <Payment
          v-else
          :redirectUrl="paymentRedirectUrl"
          :allowSkip="false"
        />
      </template>
    </Card>

    <Card class="mt-4">
      <template #content>
        <div class="grid">
          <div class="col-12 sm:col sm:flex-grow-1">
            <h2 class="mt-0 text-xl">
              Users
            </h2>
          </div>
          <div class="col-12 sm:col sm:flex-grow-0">
            <Button
              class="w-full justify-content-center text-wrap-nowrap"
              severity="success"
              @click="addUserDialogData.visible = true"
            >
              Invite Admin
            </Button>
          </div>
        </div>

        <div class="mt-4">
          <DataTable
            :value="compiledUsers"
            dataKey="id"
          >
            <Column
              field="email"
              header="Email"
            />
            <Column
              field="role"
              header="Role"
            />
            <Column
              field="status"
              header="Status"
            >
              <template #body="slotProps">
                <Chip :class="slotProps.data.statusChipClass">{{ slotProps.data.status }}</Chip>
              </template>
            </Column>
            <Column
              class="w-1p"
            >
              <template #body="slotProps">
                <Button
                  icon="pi pi-ellipsis-v"
                  text
                  plain
                  aria-haspopup="true"
                  :aria-controls="`user-row-menu-${slotProps.data.id}`"
                  @click="(event) => this.$refs[`userRowMenu${slotProps.data.id}`].toggle(event)"
                />
                <Menu
                  :id="`user-row-menu-${slotProps.data.id}`"
                  class="right-align-menu"
                  :ref="`userRowMenu${slotProps.data.id}`"
                  popup
                  :model="userRowOptionsDictionary[slotProps.data.id]"
                />
              </template>
            </Column>
          </DataTable>
        </div>
      </template>
    </Card>

    <UserFormDialog
      v-model:visible="addUserDialogData.visible"
      :isSubmitting="addUserDialogData.isSubmitting"
      :roleOptions="rolesStore.advertiserRoleOptions"
      :disableNameFields="true"
      header="Invite Admin User"
      :canEditRole="false"
      @submit="onSubmitAddUser"
    />

    <UserFormDialog
      v-model:visible="editUserDialogData.visible"
      :isSubmitting="editUserDialogData.isSubmitting"
      :user="editUserDialogData.user"
      :roleOptions="rolesStore.advertiserRoleOptions"
      :disableNameFields="editUserDialogData.user && editUserDialogData.user.fname === null"
      @submit="onSubmitEditUser"
      :canEditRole="false"
    />

    <ConfirmDialog
      v-model:visible="deleteUserDialogData.visible"
      header="Confirm Delete"
      :isSubmitting="deleteUserDialogData.isSubmitting"
      @confirm="onSubmitDeleteUser"
    >
      <div v-html="deleteUserDialogData.body" />
    </ConfirmDialog>
  </PageContainer>
</template>

<script>
import { mapStores } from 'pinia';
import { array, object, string } from 'yup';
import { v4 as uuidv4 } from 'uuid';
import { loadStripe } from '@stripe/stripe-js';
import { INVALID_FORM_SUBMISSION_MESSAGE } from '@/utils/messages';
import {
  useMyUserStore,
  useUsersStore,
  useRolesStore,
  useCategoriesStore,
  useSettingsStore,
} from '@/stores';
import * as api from '@/api';
import ConfirmDialog from '@/components/confirmDialog';
import AlertDialog from '@/components/alertDialog';
import UserFormDialog from '@/components/userFormDialog';
import { parseMessageFromError } from '@/utils/errors';
import Payment from '@/components/stripe/payment';
import { IAB_CATEGORY_OPTIONS } from '@/constants';
import CategoryInput from '@/components/categoryInput';
import AvailablePaymentMessage from './components/AvailablePaymentMessage';

const USER_STATUS_ACTIVE = 'Active';
const USER_STATUS_PENDING = 'Pending';
const PUBLISHABLE_KEY = process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY;

export default {
  components: {
    UserFormDialog,
    ConfirmDialog,
    Payment,
    AvailablePaymentMessage,
    CategoryInput,
  },
  computed: {
    ...mapStores(
      useMyUserStore,
      useUsersStore,
      useRolesStore,
      useCategoriesStore,
      useSettingsStore,
    ),
    compiledUsers() {
      const users = this.myUserStore.myUser && this.myUserStore.myUser.organization_id
        ? this.usersStore.getUsersByOrganizationId(this.myUserStore.myUser.organization_id)
        : [];

      const { roles } = this.rolesStore;

      return users.map((user) => {
        const matchingRole = roles.find((role) => Number(role.id) === Number(user.role_id));

        const status = user.fname !== null
          ? USER_STATUS_ACTIVE
          : USER_STATUS_PENDING;
        const statusChipClass = user.fname !== null
          ? 'bg-green-400 text-white'
          : 'bg-blue-100 text-blue-500';

        return {
          ...user,
          role: matchingRole
            ? matchingRole.name
            : 'Unknown',
          status,
          statusChipClass,
        };
      });
    },
    userRowOptionsDictionary() {
      return this.compiledUsers.reduce((acc, item) => {
        acc[item.id] = [
          {
            label: 'Edit',
            command: () => this.onEditUser(item),
          },
          {
            label: 'Delete',
            command: () => this.onDeleteUser(item),
          },
        ];

        return acc;
      }, {});
    },
    showAvailablePaymentMessage() {
      return this.myUserStore.organizationHasPaymentMethod
        || (!!this.setupIntentClientSecret && this.wasStripeSetupSuccessful);
    },
    isSubmitOrganizationDisabled() {
      return this.myUserStore.organizationTapCategoriesAreLoading;
    },
  },
  data() {
    return {
      schema: object({
        name: string().required('Name is required'),
        iabCodes: array().min(1, 'Must choose at least 1 IAB category').required(),
      }),
      organizationForm: {
        name: '',
        iabCodes: [],
      },
      organizationFormIsSubmitting: false,
      addUserDialogData: {
        visible: false,
        isSubmitting: false,
      },
      editUserDialogData: {
        visible: false,
        isSubmitting: false,
        user: null,
      },
      deleteUserDialogData: {
        visible: false,
        isSubmitting: false,
        user: null,
      },
      paymentRedirectUrl: window.location.href,
      setupIntentClientSecret: null,
      wasStripeSetupSuccessful: false,
      IAB_CATEGORY_OPTIONS,
    };
  },
  mounted() {
    this.getOrganizationsUsers();
    this.rolesStore.getRoles();

    if (this.myUserStore.organizationHasTapAdvertiser) {
      this.getOrganizationCategories();
    }

    this.organizationForm = {
      name: this.myUserStore.myOrganization
        ? this.myUserStore.myOrganization.name
        : '',
      iabCodes: [],
    };

    if (this.$route.query.setup_intent_client_secret) {
      this.setupIntentClientSecret = this.$route.query.setup_intent_client_secret;
      this.setStripePaymentSuccess();
    }
  },
  methods: {
    async setStripePaymentSuccess() {
      try {
        const stripe = await loadStripe(PUBLISHABLE_KEY);

        const {
          setupIntent,
          error,
        } = await stripe.retrieveSetupIntent(this.setupIntentClientSecret);

        if (setupIntent && setupIntent.status === 'succeeded') {
          this.wasStripeSetupSuccessful = true;
        } else if (error && error.message) {
          this.$toast.add({
            severity: 'error',
            detail: error.message,
          });
        } else if (error) {
          this.$toast.add({
            severity: 'error',
            detail: 'Errord confirming payment setup status.',
          });
        }
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          detail: 'Errord confirming payment setup status.',
        });
      }
    },
    getOrganizationsUsers() {
      return this.usersStore.getUsers({
        organizationId: this.myUserStore.myOrganization
          ? this.myUserStore.myOrganization.id
          : undefined,
      });
    },
    async getOrganizationCategories() {
      try {
        await this.myUserStore.getOrganizationTapCategories();

        this.organizationForm.iabCodes = [...this.myUserStore.organizationTapCategories];
      } catch (error) {
        const message = parseMessageFromError(error, 'Error reading organization categories.');

        this.$toast.add({
          severity: 'error',
          detail: message,
        });
      }
    },
    onInvalidSubmit() {
      this.$toast.add({
        severity: 'warn',
        detail: INVALID_FORM_SUBMISSION_MESSAGE,
      });
    },
    async onSubmitOrganizationInfo() {
      try {
        this.organizationFormIsSubmitting = true;
        const organizationId = this.myUserStore.myOrganization.id;

        // only call API if name has changed
        if (this.organizationForm.name !== this.myUserStore.myOrganization.name) {
          await api.updateOrganization({
            organizationId,
            name: this.organizationForm.name,
          });
        }

        if (this.myUserStore.organizationHasTapAdvertiser) {
          await api.updateAdvertiserCategories({
            organizationId,
            iabCodes: this.organizationForm.iabCodes,
          });
        } else {
          await api.createAdvertiser({
            organizationId,
            name: `${this.organizationForm.name} ${uuidv4()}`,
            iabCodes: this.organizationForm.iabCodes,
          });
        }

        await this.myUserStore.getMyUser();

        if (this.myUserStore.organizationHasTapAdvertiser) {
          await this.myUserStore.getOrganizationTapCategories();
        }

        this.$toast.add({
          severity: 'success',
          detail: 'Successfully updated organization',
        });
      } catch (error) {
        const message = parseMessageFromError(error, 'Error updating organization.');

        this.$toast.add({
          severity: 'error',
          detail: message,
        });
      } finally {
        this.organizationFormIsSubmitting = false;
      }
    },
    async onSubmitAddUser({ userForm }) {
      try {
        this.addUserDialogData.isSubmitting = true;

        const roleId = this.rolesStore.advertiserAdminRole.id;

        const invitedUser = await api.inviteUser({
          organizationId: this.myUserStore.myOrganization.id,
          email: userForm.email,
        });
        await api.updateUser({
          userId: invitedUser.user_id,
          roleId,
        });

        this.$toast.add({
          severity: 'success',
          detail: 'Successfully invited user.',
        });

        this.addUserDialogData.visible = false;

        this.getOrganizationsUsers();
      } catch (error) {
        const message = parseMessageFromError(error, 'Error inviting user.');

        this.$toast.add({
          severity: 'error',
          detail: message,
        });
      } finally {
        this.addUserDialogData.isSubmitting = false;
      }
    },
    onEditUser(user) {
      this.editUserDialogData = {
        ...this.editUserDialog,
        visible: true,
        user,
      };
    },
    async onSubmitEditUser({ user, userForm }) {
      try {
        this.editUserDialogData.isSubmitting = true;

        await api.updateUser({
          userId: user.id,
          firstName: userForm.firstName || '',
          lastName: userForm.lastName || '',
          email: userForm.email || '',
          roleId: userForm.roleId || undefined,
        });

        this.$toast.add({
          severity: 'success',
          detail: 'Successfully updated user.',
        });

        this.editUserDialogData.visible = false;
        this.editUserDialogData.user = null;

        this.getOrganizationsUsers();
      } catch (error) {
        const message = parseMessageFromError(error, 'Error updating user.');

        this.$toast.add({
          severity: 'error',
          detail: message,
        });
      } finally {
        this.editUserDialogData.isSubmitting = false;
      }
    },
    onDeleteUser(user) {
      const userIsSelf = user.id === this.myUserStore.myUser.id;
      if (userIsSelf) {
        this.$dialog.open(AlertDialog, {
          data: {
            text: 'You cannot remove yourself from the organization.',
          },
          props: {
            header: 'Alert',
            modal: true,
            dismissableMask: true,
            closable: true,
          },
        });
        return;
      }

      const isLastActiveUser = user.status === USER_STATUS_ACTIVE && this.compiledUsers
        .filter((item) => item.status === USER_STATUS_ACTIVE).length === 1;
      if (isLastActiveUser) {
        this.$dialog.open(AlertDialog, {
          data: {
            text: 'You cannot remove the last active user of an organization.',
          },
          props: {
            header: 'Alert',
            modal: true,
            dismissableMask: true,
            closable: true,
          },
        });
        return;
      }
      // TODO - can't delete admin user

      this.deleteUserDialogData = {
        ...this.deleteUserDialogData,
        visible: true,
        body: `<p>Are you sure you want to delete this user: <strong>${user.email}</strong>?</p>
          <p>This action cannot be undone.</p>`,
        user,
      };
    },
    async onSubmitDeleteUser() {
      try {
        this.deleteUserDialogData.isSubmitting = true;

        await api.deleteUser(this.deleteUserDialogData.user.id);

        this.$toast.add({
          severity: 'success',
          detail: 'Successfully deleted user.',
        });

        this.deleteUserDialogData.visible = false;
        this.deleteUserDialogData.user = null;

        this.getOrganizationsUsers();
      } catch (error) {
        const message = parseMessageFromError(error, 'Error deleting user.');

        this.$toast.add({
          severity: 'error',
          detail: message,
        });
      } finally {
        this.deleteUserDialogData.isSubmitting = false;
      }
    },
  },
};
</script>
